import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAlbumPhotoBackground } from '../../../../actions/album-photo-backgrounds';
import useHorizontalSwipe from '../../../../services/general/useHorizontalSwipe';

const usePhotoGridViewerPrintBackgrounds = ({ photoId, printBackgrounds }) => {
  const dispatch = useDispatch();
  const selectedPrintBackgroundId = useSelector(
    ({ albumPhotoBackgrounds }) =>
      albumPhotoBackgrounds?.printBackgrounds?.[photoId],
  );
  const setSelectedPrintBackgroundId = useCallback((...args) =>
    dispatch(setAlbumPhotoBackground(...args)),
  );

  useEffect(() => {
    if (
      !printBackgrounds?.find(({ id }) => id === selectedPrintBackgroundId) &&
      printBackgrounds?.length > 0
    ) {
      setSelectedPrintBackgroundId({
        photoId,
        printBackgroundId: printBackgrounds[0].id,
      });
    }
  }, [printBackgrounds, photoId]);

  const selectedPrintBackground = useMemo(
    () =>
      printBackgrounds?.find(
        background => background.id === selectedPrintBackgroundId,
      ),
    [printBackgrounds, selectedPrintBackgroundId],
  );

  const onPrevBackground = useCallback(() => {
    if (!printBackgrounds || printBackgrounds?.length === 0) {
      return;
    }

    const currentIndex = printBackgrounds.indexOf(selectedPrintBackground);
    if (currentIndex - 1 < 0) {
      setSelectedPrintBackgroundId({
        photoId,
        printBackgroundId: printBackgrounds[printBackgrounds.length - 1].id,
      });
    } else {
      setSelectedPrintBackgroundId({
        photoId,
        printBackgroundId: printBackgrounds[currentIndex - 1].id,
      });
    }
  }, [selectedPrintBackground, printBackgrounds, photoId]);

  const onNextBackground = useCallback(() => {
    if (!printBackgrounds || printBackgrounds?.length === 0) {
      return;
    }

    const currentIndex = printBackgrounds.indexOf(selectedPrintBackground);
    if (currentIndex + 1 === printBackgrounds.length) {
      setSelectedPrintBackgroundId({
        photoId,
        printBackgroundId: printBackgrounds[0].id,
      });
    } else {
      setSelectedPrintBackgroundId({
        photoId,
        printBackgroundId: printBackgrounds[currentIndex + 1].id,
      });
    }
  }, [selectedPrintBackground, printBackgrounds, photoId]);

  const swipeHandlers = useHorizontalSwipe({
    onSwipeRight: onPrevBackground,
    onSwipeLeft: onNextBackground,
  });

  return {
    selectedPrintBackground,
    onPrevBackground,
    onNextBackground,
    swipeHandlers,
  };
};

export default usePhotoGridViewerPrintBackgrounds;
